import React, { useEffect, useRef, useMemo } from "react";
import FolderImg from "../../assets/images/FolderLogoClean.png";
import MyDocumentTile from "../MyDocumentTile";
import PartnerDocumentTile from "../PartnerDocumentTile";
import { tracker } from "../../services/mixpanel";
import { trackScrollDepth } from "../../services/scrollTracker";
import useHasPermission from "../../hooks/useHasPermission";
import { useGetMarketingResourcesQuery, useGetMarketingResourcePublicUrlsQuery } from "../../services/api";
import { FileType, PurlModule } from "../../utils/documentsModules";
import DynamicIcon from "./DynamicIcon";
import { useResourceClickHandler } from "../../hooks/useResourceClickHandler";

const DynamicOverviewTile = ({
  openOverlay,
  openAnimatedOverview,
  openOverlay1,
  partnerDocuments,
}: OverViewProps) => {
  const { hasPermission } = useHasPermission();
  const { data, isLoading } = useGetMarketingResourcesQuery(PurlModule.OVERVIEW);

  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const cleanup = trackScrollDepth({
      name: "Overview",
      element: container,
    });

    return cleanup;
  }, []);

  const handleDocumentModal = (
    id: string,
    heading: string,
    showSidebar?: boolean,
    message?: string,
    title?: string,
  ) => {
    tracker.dashboard.documentModal.onOpen(heading);
    openOverlay1(
      <MyDocumentTile
        title={title}
        folder={{ id: id }}
        showSidebar={showSidebar}
        heading={heading}
        message={message}
      />
    );
  };

  const handlePartnerDocumentModal = (
    heading: string,
    showSidebar?: boolean
  ) => {
    tracker.dashboard.documentModal.onOpen(heading);
    openOverlay1(<PartnerDocumentTile heading={heading} />);
  };

  const handlePdfModal = (name: string, link: string) => {
    tracker.dashboard.overview.pdfModal.onOpen(name);
    openOverlay(link);
  };

  const handleVideoModal = (name: string, link: string) => {
    tracker.dashboard.overview.videoModal.onOpen(name);
    openAnimatedOverview(link);
  };

  const onPartnerDocumentsClick = () => {
    handlePartnerDocumentModal("Partner Documents", false);
  };

  const { handleResourceClick } = useResourceClickHandler({
    moduleName: PurlModule.OVERVIEW,
    openAnimatedOverview,
    handleVideoModal,
    handlePdfModal,
    handleDocumentModal,
  });



  const sortedResources = data?.data || [];

  const iconKeys = useMemo(() => {
    return sortedResources
      .filter((item: IPurlMarketingResource) => item.isActive && (!item.key || hasPermission(item.key as keyof ITilePermissions)))
      .map((r: IPurlMarketingResource) => r.icon)
      .filter(Boolean) as string[];
  }, [sortedResources, hasPermission]);

  const { data: urlsData, isLoading: isUrlsLoading } = useGetMarketingResourcePublicUrlsQuery(iconKeys, {
    skip: iconKeys.length === 0,
  });

  const urlsMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (urlsData?.data) {
      urlsData.data.forEach((item) => {
        map[item.key] = item.url;
      });
    }
    return map;
  }, [urlsData]);

  return (
    <div
      ref={containerRef}
      className={`bg-[#FEFCFB] rounded-xl w-full pt-1 shadow-lg h-[390px] overflow-y-auto`}
    >
      <h2 className="text-[25px] text-black fond-bold p-1 mb-[8px]">
        Overview
      </h2>
      <div className="grid gap-y-3 grid-cols-3 border-t-4 border-[#EFEFF4] p-2 pt-4">
        {isLoading && (
          <div className="col-span-3 text-center text-sm text-gray-500 py-4">
            Loading Overview Resources...
          </div>
        )}

        {!isLoading &&
          sortedResources.map((item: IPurlMarketingResource) => {
            if (!item.isActive) return null;
            if (item.key && !hasPermission(item.key as keyof ITilePermissions)) return null;

            return (
              <div
                key={item._id || item.key}
                className="flex flex-col items-center cursor-pointer"
                onClick={() => handleResourceClick(item)}
              >
                {item.icon ? (
                   <DynamicIcon 
                     url={urlsMap[item.icon]} 
                     isLoading={isUrlsLoading}
                     className="h-[50px] w-auto max-w-[75px]" 
                     alt={item.title} 
                   />
                ) : (
                  <img className="h-[50px]" src={FolderImg} alt={item.title} />
                )}
                <h3 className="text-center text-[13px] mt-4 uppercase">
                  {item.title}
                </h3>
              </div>
            );
          })}

        {!isLoading && hasPermission("partnerDocuments") && partnerDocuments && (
          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={onPartnerDocumentsClick}
          >
            <img className="h-[50px]" src={FolderImg} alt="Partner Documents" />
            <h3 className="text-center text-[13px] mt-4 uppercase">
              PARTNER DOCUMENTS
            </h3>
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicOverviewTile;
